import * as d3 from 'd3';
import moment from 'moment';
import React, { useEffect, useMemo } from 'react';

import theme from 'theme';
import { getCurrentUtcDate } from 'utils/dateTimeHelpers';

import {
  d3AlignLastTickWithRhsOfLastBar,
  d3ApplyAxisTypogaphy,
  d3ComputeBarChartMeasurementsXAxis,
  d3EnterExitBars,
  d3SetTickLabelsInward,
  d3SetTickStyle,
  Prop2d3GraphPadding,
  XAxisTickDimensions,
} from '../_common/d3GraphHelpers';
import { D3GraphPadding, SparklineDatumType } from '../_common/DiseaseActivityModels';
import { IterateWithinDateWindow } from '../_common/graphUtils';

// =====================================================================================================================================
const DEBUG_SVG = 0;
const CurDate = getCurrentUtcDate();
const CurYear = moment(CurDate).get('year');
const BarThicknesses = [3, 14];
const DefaultMaxYValue = 10;
const axisColor = theme.colors.stone30;
const axisLabelColor = theme.colors.stone50;
const BarColorCases = theme.colors.sea40;
const BarColorNearby = theme.colors.seafoam80;
// =====================================================================================================================================

type XAxesTickBreakpointsInDays = {
  week: number;
  month: number;
  year: number;
};
type BdSparkGraphBarsProps = {
  graphData: SparklineDatumType[];
  minDate: Date;
  maxDate?: Date;
  width: number;
  height: number;
  padding?: number | D3GraphPadding;
  rounded?: boolean;
  isDateTicksUppercase?: boolean;
  xAxesTickBreakpoints?: XAxesTickBreakpointsInDays;
  aggregationDays?: number;
  isLoading?: boolean;
  xAxisTypography?: string;
  xAxisTickDimensions?: XAxisTickDimensions;
};

export const BdSparkGraphBars: React.FC<BdSparkGraphBarsProps> = ({
  graphData,
  minDate,
  maxDate = CurDate,
  width: widthSVG,
  height: heightSVG,
  padding: paddingProp = 10,
  rounded = false,
  isDateTicksUppercase = false,
  xAxesTickBreakpoints: xAxesTickBreakpointsProp,
  aggregationDays = 1,
  isLoading = false,
  xAxisTypography = 'default',
  xAxisTickDimensions = { thickness: 1, length: 3 },
}) => {
  const padding = useMemo(() => Prop2d3GraphPadding(paddingProp), [paddingProp]);
  const xAxesTickBreakpoints: XAxesTickBreakpointsInDays = {
    week: 60,
    month: 6 * 30,
    year: undefined, // NOTE: doesn't matter, year is `else` clause
    ...xAxesTickBreakpointsProp,
  };

  const ref = React.useRef();
  const width = widthSVG - padding.left - padding.right;
  const height = heightSVG - padding.top - padding.bottom;

  // ------------------ filter data
  const graphData2 = useMemo(
    () => IterateWithinDateWindow(graphData || [], minDate, maxDate),
    [graphData, minDate, maxDate]
  );

  const maxYValue = useMemo(
    () =>
      graphData2 && graphData2.length
        ? Math.max(
            d3.max(graphData2, d => Math.max(d.cases ?? 0, d.nearby ?? 0)),
            1
          )
        : DefaultMaxYValue,
    [graphData2]
  );
  const [barThicknessMin, barThicknessMax] = BarThicknesses;
  const [barWidth, barMargin, nDays] = useMemo(
    () =>
      d3ComputeBarChartMeasurementsXAxis(
        minDate,
        maxDate,
        width,
        barThicknessMin,
        barThicknessMax,
        aggregationDays
      ),
    [aggregationDays, barThicknessMax, barThicknessMin, maxDate, minDate, width]
  );

  const scaleX = useMemo(
    () =>
      d3
        .scaleTime()
        .domain([minDate, maxDate])
        .range([0, width - barWidth]),
    [barWidth, maxDate, minDate, width]
  );
  const scaleY = useMemo(
    () => d3.scaleLinear().domain([0, maxYValue]).range([height, 0]).clamp(true),
    [height, maxYValue]
  );

  // ------------------------------------------------ svg initialization -------------------------------------------------------------------------------------
  useEffect(() => {
    const node = ref.current;
    const svg = d3.select(node);

    DEBUG_SVG &&
      svg.append('rect').attr('width', '100%').attr('height', '100%').attr('fill', '#fcc');

    svg
      .append('g')
      .attr('class', 'x-axis-bg')
      .attr('transform', `translate(${padding.left}, ${height + padding.top})`);
    svg
      .append('g')
      .attr('class', 'chart-root')
      .attr('transform', `translate(${padding.left},${padding.top})`);
    svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(${padding.left}, ${height + padding.top})`);
    svg
      .append('g')
      .attr('class', 'chart-fg-loading')
      .attr(
        'transform',
        `translate(${padding.left},${padding.top + height - 60}), scale(${width / 92.0}, 1)`
      ) // NOTE: loading skeleton svg is 92x60
      .style('display', 'none').html(`
        <path fill-rule="evenodd" clip-rule="evenodd" d="M31.1992 26C31.1992 24.8954 32.0947 24 33.1992 24H35.1992C36.3038 24 37.1992 24.8954 37.1992 26V60H31.1992V26ZM2 40C0.895431 40 0 40.8954 0 42V60H7V42C7 40.8954 6.10457 40 5 40H2ZM63.3984 40C62.2939 40 61.3984 40.8954 61.3984 42V60H68.3984V42C68.3984 40.8954 67.503 40 66.3984 40H63.3984ZM17.5996 32C16.495 32 15.5996 32.8954 15.5996 34V60H22.5996V34C22.5996 32.8954 21.7042 32 20.5996 32H17.5996ZM76.999 34C76.999 32.8954 77.8945 32 78.999 32H81.999C83.1036 32 83.999 32.8954 83.999 34V60H76.999V34ZM47.7988 32C46.6943 32 45.7988 32.8954 45.7988 34V60H52.7988V34C52.7988 32.8954 51.9034 32 50.7988 32H47.7988ZM7.7998 38C7.7998 36.8954 8.69524 36 9.7998 36H12.7998C13.9044 36 14.7998 36.8954 14.7998 38V60H7.7998V38ZM71.1982 36C70.0937 36 69.1982 36.8954 69.1982 38V60H76.1982V38C76.1982 36.8954 75.3028 36 74.1982 36H71.1982ZM84.7979 30C84.7979 28.8954 85.6933 28 86.7979 28H89.7979C90.9024 28 91.7979 28.8954 91.7979 30V60H84.7979V30ZM39.999 28C38.8945 28 37.999 28.8954 37.999 30V60H44.999V30C44.999 28.8954 44.1036 28 42.999 28H39.999ZM23.3994 30C23.3994 28.8954 24.2948 28 25.3994 28H28.3994C29.504 28 30.3994 28.8954 30.3994 30V60H23.3994V30ZM55.5986 36C54.4941 36 53.5986 36.8954 53.5986 38V60H60.5986V38C60.5986 36.8954 59.7032 36 58.5986 36H55.5986Z" fill="#EDF0F2"/>
      `);
  }, []);

  // ------------------------------------------------- loading state ------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.select('.chart-fg-loading').style('display', isLoading ? 'block' : 'none');
    svg.select('.chart-root').style('display', !isLoading ? 'block' : 'none');
    svg.select('.x-axis-bg').style('display', !isLoading ? 'block' : 'none');
  }, [isLoading]);

  // ------------------------------------------------- render axes ------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(ref.current);
    const xAxisBg = d3
      .axisBottom(scaleX)
      .ticks(
        nDays < xAxesTickBreakpoints.week
          ? d3.timeSunday
          : nDays < xAxesTickBreakpoints.month
          ? d3.timeMonth
          : d3.timeYear
        // NOTE: d3.time.quarter not part of standard d3
      )
      .tickSize(-height)
      .tickFormat(x => ``);

    const placeholderAxisXBg = svg.select('.x-axis-bg');
    placeholderAxisXBg.call(xAxisBg);
    placeholderAxisXBg
      .selectAll('.tick')
      .style('stroke-dasharray', '2 2')
      .attr('color', BarColorCases);
    placeholderAxisXBg.select('.domain').remove(); // NOTE: removes the bottom axis
    // placeholderAxisXBg.selectAll('text').attr('font-size', '0.3em'); // DEBUG

    type TickFormatFn = (date: Date) => string;
    const fixTickCasing = (fn: TickFormatFn) =>
      isDateTicksUppercase ? x => fn(x).toUpperCase() : fn;

    const xAxis = d3
      .axisBottom(scaleX)
      .tickValues([minDate, maxDate])
      .tickSizeOuter(0)
      .tickFormat(
        minDate.getFullYear() === CurYear
          ? fixTickCasing(d3.timeFormat('%b %d')) // LINK: https://github.com/d3/d3-time-format#locale_format
          : fixTickCasing(d3.timeFormat('%b %Y'))
      );

    const placeholderAxisX = svg.select('.x-axis');
    placeholderAxisX.selectAll('*').remove();
    placeholderAxisX
      .call(xAxis)
      .call(d3ApplyAxisTypogaphy(axisLabelColor, xAxisTypography))
      .attr('color', axisColor);
    placeholderAxisX.selectAll('.tick').call(d3SetTickLabelsInward);
    placeholderAxisX.selectAll('.tick').call(d3AlignLastTickWithRhsOfLastBar(barWidth));
    placeholderAxisX.call(d3SetTickStyle(xAxisTickDimensions));

    placeholderAxisX.select('.domain').remove();
    placeholderAxisX.append('line').attr('x2', width).attr('stroke', axisColor);

    if (isDateTicksUppercase) {
      placeholderAxisX.style('text-transform', 'uppercase');
    }
  }, [
    nDays,
    scaleX,
    scaleY,
    width,
    height,
    xAxesTickBreakpoints.week,
    xAxesTickBreakpoints.month,
    minDate,
    maxDate,
    barWidth,
    isDateTicksUppercase,
    xAxisTypography,
  ]);

  // ------------------------------------------------- render bars of data ------------------------------------------------------------------------------------
  useEffect(() => {
    const svg = d3.select(ref.current);

    const [bars, barsEntred, barsExited] = d3EnterExitBars(
      svg.select<SVGGElement>('.chart-root'),
      graphData2,
      'g'
    );
    barsEntred.append('rect').attr('class', 'cases');
    barsEntred.append('rect').attr('class', 'nearby');

    bars
      .select('.cases')
      .attr('x', d => scaleX(d.date))
      .attr('y', d => scaleY(d.cases || 0))
      .attr('height', d => height - scaleY(d.cases || 0))
      .attr('width', barWidth)
      .attr('fill', BarColorCases);
    bars
      .select('.nearby')
      .attr('x', d => scaleX(d.date))
      .attr('y', d => scaleY(d.nearby || 0))
      .attr('height', d => height - scaleY(d.nearby || 0))
      .attr('width', barWidth)
      .attr('fill', BarColorNearby);
  }, [barWidth, graphData2, height, rounded, scaleX, scaleY]);

  return <svg ref={ref} width={widthSVG} height={heightSVG} />;
};
